import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    ApplicationFacade,
    Itinerary,
    JourneyMapPlan,
    JourneyMapPlanLeg,
    JourneyPlannerItineraryTransportMode,
    Leg,
    OpenTripPlannerFacade,
    OtpUtil,
    PolyLineStyle,
    StopMarker,
    TransportMode,
    TripDetailsTime
} from '@ridango/components';
import { filter, first, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import {
    AppStartupService,
    BaseComponent,
    DisruptionMessage,
    DisruptionMessageEntity,
    DisruptionMessageEntityType,
    DisruptionMessagesService,
    DisruptionMessageType,
    LatLng,
    matchesStopId
} from '@ridango/core';
import { JourneyPlannerMapService, MapApiService } from '@ridango/widgets';
import Env from '@ridango/core/env';
import { Temporal } from '@js-temporal/polyfill';
import { StatusBarService } from '@ridango/core/lib/services/status-bar.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
    templateUrl: './journey-planner-itinerary-details.component.html',
    styleUrls: ['./journey-planner-itinerary-details.component.scss']
})
export class JourneyPlannerItineraryDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
    itinerary: Itinerary;
    itinerary$: Observable<Itinerary>;
    tripIdToDetailsMap: Map<string, TripDetailsTime[]> = new Map();
    currentTime$: BehaviorSubject<number>;
    minimumVisibleDistance: number = this.env?.JOURNEY_PLANNER?.MINIMUM_VISIBLE_DISTANCE ?? 0;
    assetsPath$ = this.applicationFacade.assetsPath$;
    fullShowMap = this.journeyPlannerMapService.isFullMapView$;
    modalBreakpointOffset = 75;

    constructor(
        public journeyPlannerMapService: JourneyPlannerMapService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private statusBarService: StatusBarService,
        private applicationFacade: ApplicationFacade,
        @Inject('ENV') public env: Env,
        private mapService: MapApiService,
        private appStartupService: AppStartupService,
        private router: Router,
        private translate: TranslateService,
        private disruptionMessagesService: DisruptionMessagesService
    ) {
        super();
    }

    ngOnInit(): void {
        this.resolveItineraryOrRedirect();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.journeyPlannerMapService.setViewMap(false);
        this.statusBarService.showStatusBar();
        this.openTripPlannerFacade.clearJourneyMapPlan();
        this.openTripPlannerFacade.setSearchDisabled(false);
    }
    getCurrentTimeInMs = () =>
        Temporal.Now.instant().toZonedDateTimeISO(this.appStartupService.getTimezone()).epochMilliseconds;

    private resolveItineraryOrRedirect(): void {
        const itinerary$ = this.openTripPlannerFacade.selectedItineraryForDetails$;

        itinerary$
            .pipe(take(1))
            .subscribe((itinerary) => (itinerary ? this.initView(itinerary$) : this.redirectToJourneyPlanner()));
    }

    private redirectToJourneyPlanner() {
        this.router.navigate([this.translate.currentLang, 'journey-planner']);
    }

    private initView(itinerary$: Observable<Itinerary>): void {
        this.mapService.setCenteringEnabled(false);
        this.currentTime$ = new BehaviorSubject(this.getCurrentTimeInMs());
        this.setItinerary(itinerary$);
        this.startCurrentTimeTick();
        this.openTripPlannerFacade.setSearchDisabled(true);
    }

    private setItinerary(itinerary$: Observable<Itinerary>): void {
        this.openTripPlannerFacade.clearJourneyMapPlan();

        this.itinerary$ = itinerary$.pipe(
            first((itinerary): itinerary is Itinerary => !!itinerary),
            tap(() => this.mapService.doResize()),
            switchMap((itinerary) => {
                const legs = itinerary.legs.filter((l) =>
                    l.distance && l.mode === TransportMode.Walk ? l.distance > this.minimumVisibleDistance : true
                );

                if (this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES) {
                    return this.disruptionMessagesService.getMessages().pipe(
                        take(1),
                        map((response) => {
                            const legsWithAlerts = this.attachDisruptionMessagesToLegs(legs, response?.messages || []);
                            return { ...itinerary, legs: legsWithAlerts };
                        })
                    );
                } else {
                    return of({ ...itinerary, legs });
                }
            }),
            tap((itinerary) => this.setPlanOnMap(itinerary)),
            shareReplay(1)
        );
    }

    private attachDisruptionMessagesToLegs(legs: Leg[], disruptionMessages: DisruptionMessage[]): Leg[] {
        const result = legs.map((leg) => {
            const alerts = this.getAlertsForLeg(leg, disruptionMessages);
            return {
                ...leg,
                disruptionMessages: alerts
            };
        });

        return result;
    }

    private getAlertsForLeg(leg: Leg, disruptionMessages: DisruptionMessage[]): DisruptionMessage[] {
        const legDisruptionMessages = [];
        if (leg.mode !== TransportMode.Walk) {
            disruptionMessages?.forEach((message) => {
                if (this.checkAlertBus(message, leg) || this.checkAlertStop(message, leg)) {
                    legDisruptionMessages.push(message);
                }
            });
        }
        return legDisruptionMessages;
    }

    private checkAlertBus(message: DisruptionMessage, leg: Leg): boolean {
        const isBusAlert =
            message.messageType === DisruptionMessageType.ALERT_BUS ||
            message.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;

        return (
            isBusAlert &&
            message.informedEntities.some((entity) => this.matchesRouteEntity(entity, leg)) &&
            this.isAlertWithinActivePeriod(leg, message)
        );
    }

    private checkAlertStop(message: DisruptionMessage, leg: Leg): boolean {
        const isStopAlert =
            message.messageType === DisruptionMessageType.ALERT_STOP ||
            message.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;

        return (
            isStopAlert &&
            message.informedEntities.some((entity) => this.matchesStopEntity(entity, leg)) &&
            this.isAlertWithinActivePeriod(leg, message)
        );
    }

    private matchesRouteEntity(entity: DisruptionMessageEntity, leg: Leg): boolean {
        return entity.type === DisruptionMessageEntityType.ROUTE && entity.routeShortName === leg.routeShortName;
    }

    private matchesStopEntity(entity: DisruptionMessageEntity, leg: Leg): boolean {
        if (entity.type !== DisruptionMessageEntityType.STOP) {
            return false;
        }

        const stopIdsToMatch = this.collectLegStopIds(leg);
        return stopIdsToMatch.some((stopId) => matchesStopId(entity.stopId, stopId));
    }

    private collectLegStopIds(leg: Leg): string[] {
        const stops = [leg.from, leg.to, ...(leg.intermediateStops || [])].filter(Boolean);

        return stops.flatMap((stop) => [stop.stopCode, stop.stopId]).filter(Boolean);
    }

    private isAlertWithinActivePeriod(leg: Leg, message: DisruptionMessage): boolean {
        const legStartTime = Temporal.Instant.fromEpochMilliseconds(leg.startTime).epochMilliseconds;
        const messageActivePeriodStart = Temporal.Instant.from(message.activePeriodStart.toString()).epochMilliseconds;
        const messageActivePeriodEnd = Temporal.Instant.from(message.activePeriodEnd.toString()).epochMilliseconds;

        return legStartTime >= messageActivePeriodStart && legStartTime <= messageActivePeriodEnd;
    }

    private startCurrentTimeTick(): void {
        interval(this.env.JOURNEY_PLANNER.UPDATE_INTERVAL_SECONDS * 1000)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.currentTime$.next(this.getCurrentTimeInMs());
            });
    }

    private setPlanOnMap(itinerary: Itinerary) {
        this.openTripPlannerFacade.location$
            .pipe(filter((p) => !!p))
            .pipe(take(1))
            .subscribe((userLocation) => {
                const firstLeg = itinerary.legs[0];
                const lastLeg = itinerary.legs[itinerary.legs.length - 1];
                const plan: JourneyMapPlan = {
                    userLocation: userLocation.coordinates,
                    journeyOriginLocation: { lat: firstLeg.from.lat, lng: firstLeg.from.lon },
                    journeyDestinationLocation: { lat: lastLeg.to.lat, lng: lastLeg.to.lon },
                    legs: this.createMapLegs(itinerary)
                };
                this.openTripPlannerFacade.setJourneyMapPlan(plan);
            });
    }

    printSchedule() {
        const userAgent = navigator.userAgent;
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent) && !/CriOS|FxiOS/.test(userAgent); // True Safari on iOS or macOS
        if (isSafari) {
            // Use execCommand for true Safari on iOS and macOS
            document.execCommand('print', false, null);
        } else {
            // For all other devices (e.g., macOS Chrome/Firefox or Android), use window.print()
            window.print();
        }
    }

    private createMapLegs(itinerary: Itinerary): JourneyMapPlanLeg[] {
        return (
            itinerary?.legs
                ?.filter((l) => l.mode !== JourneyPlannerItineraryTransportMode.Wait)
                .map((leg) => {
                    const legStops = [leg.from, leg.to];

                    if (leg.intermediateStops) {
                        legStops.splice(1, 0, ...leg.intermediateStops);
                    }

                    return {
                        markers: legStops ? this.createMapMarkers(legStops) : [],
                        polyline: leg.legGeometry?.points,
                        polylineStyle:
                            leg.tripId || leg.mode === JourneyPlannerItineraryTransportMode.Flexible
                                ? PolyLineStyle.SOLID
                                : PolyLineStyle.DASHED,
                        color:
                            leg.tripId || leg.mode === JourneyPlannerItineraryTransportMode.Flexible
                                ? OtpUtil.resolveColor(leg?.icon?.color || leg.routeColor)
                                : '#838383'
                    };
                }) ?? []
        );
    }

    private createMapMarkers(intermediateStops: Leg['intermediateStops']): StopMarker[] {
        const timezone = this.appStartupService.getTimezone();
        return (
            intermediateStops.map((stop, i) => {
                const depart =
                    intermediateStops.length - 1 === i
                        ? undefined
                        : OtpUtil.unixToDate(stop.departure, timezone).toInstant().toString();
                const arrive =
                    intermediateStops.length - 1 === i
                        ? OtpUtil.unixToDate(stop.arrival, timezone).toInstant().toString()
                        : undefined;
                return {
                    name: stop.name,
                    code: stop.stopCode,
                    departureClockTime: depart,
                    arrivalClockTime: arrive,
                    latlng: { lat: stop.lat, lng: stop.lon } as LatLng
                };
            }) ?? []
        );
    }
}
